import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Mail, Send, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { contactApi } from '../services/api';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    website: '' // Honeypot field - should remain empty
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHuman, setIsHuman] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check honeypot field - if filled, it's likely a bot
    if (formData.website) {
      toast.success('Message envoyé !');
      return;
    }
    
    // Check if human checkbox is checked
    if (!isHuman) {
      toast.error('Veuillez confirmer que vous n\'êtes pas un robot');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Don't send the honeypot field
      const { website, ...dataToSend } = formData;
      await contactApi.send(dataToSend);
      toast.success('Message envoyé !', {
        description: 'Nous vous répondrons dans les plus brefs délais.'
      });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        website: ''
      });
      setIsHuman(false);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi', {
        description: 'Veuillez réessayer plus tard.'
      });
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Contact | Pam Immobilier - Courtage Suisse romande"
        description="Contactez Pam Immobilier pour une estimation personnalisée ou toute question. Disponible par téléphone au 079 133 78 98 ou par email."
      />
      <Header />
      <main>
        {/* Hero Section - Clean gradient banner */}
        <section className="relative bg-gradient-to-br from-[#8B2942] via-[#A13350] to-[#722338] overflow-hidden mt-32" style={{ height: '120px' }}>
          <div className="absolute inset-0 flex items-center z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <h1 className="text-xl md:text-2xl font-bold text-white">
                Pour une estimation personnalisée ou toute autre question
              </h1>
            </div>
          </div>
        </section>

        {/* Contact Content */}
        <section className="py-16 bg-stone-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Portrait + Contact Info Section */}
            <div className="mb-12">
              <Card className="overflow-hidden">
                <CardContent className="p-8 md:p-12">
                  <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
                    {/* Portrait with round border */}
                    <div className="flex-shrink-0">
                      <div className="w-48 h-48 md:w-56 md:h-56 rounded-full border-4 border-[#8B2942] p-1">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_14bd1402-02f4-4dac-83c7-ac89ea1f2296/artifacts/qukq9zpu_image.png"
                          alt="Pamela Moll - Courtière immobilière"
                          className="w-full h-full object-cover rounded-full"
                        />
                      </div>
                    </div>
                    
                    {/* Contact Details */}
                    <div className="text-center md:text-left">
                      <h2 className="text-2xl md:text-3xl font-bold text-[#8B2942] mb-2">Pamela Moll</h2>
                      <p className="text-gray-500 text-lg mb-6">Courtière immobilière</p>
                      
                      <div className="space-y-4">
                        <a href="tel:+41791337898" className="flex items-center justify-center md:justify-start gap-4 text-gray-700 hover:text-[#8B2942] transition-colors group">
                          <div className="p-3 rounded-lg bg-[#8B2942]/10 group-hover:bg-[#8B2942]/20 transition-colors">
                            <Phone className="w-5 h-5 text-[#8B2942]" />
                          </div>
                          <span className="text-lg">+41 79 133 78 98</span>
                        </a>
                        
                        <a href="mailto:info@pamimmobilier.ch" className="flex items-center justify-center md:justify-start gap-4 text-gray-700 hover:text-[#8B2942] transition-colors group">
                          <div className="p-3 rounded-lg bg-[#8B2942]/10 group-hover:bg-[#8B2942]/20 transition-colors">
                            <Mail className="w-5 h-5 text-[#8B2942]" />
                          </div>
                          <span className="text-lg">info@pamimmobilier.ch</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="max-w-3xl mx-auto">
              <Card>
                <CardContent className="p-8">
                    <h2 className="text-2xl font-bold text-[#8B2942] mb-4">Envoyez-moi un message</h2>
                    <p className="text-gray-600 leading-relaxed mb-6">
                      Le plus simple est de m'appeler et me laisser un message sur ma boîte vocale au{' '}
                      <a href="tel:+41791337898" className="font-semibold text-[#8B2942] hover:underline">
                        079 133 78 98
                      </a>
                      {' '}ou par écrit avec le formulaire ci-dessous.
                    </p>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name">Nom complet *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Votre nom"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="Votre email"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="phone">Téléphone</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+41 XX XXX XX XX"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="subject">Sujet *</Label>
                          <Input
                            id="subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            required
                            placeholder="Objet de votre message"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          placeholder="Votre message..."
                          rows={6}
                        />
                      </div>

                      {/* Honeypot field - hidden from humans, visible to bots */}
                      <div className="hidden" aria-hidden="true">
                        <Label htmlFor="website">Website</Label>
                        <Input
                          id="website"
                          name="website"
                          value={formData.website}
                          onChange={handleChange}
                          tabIndex={-1}
                          autoComplete="off"
                        />
                      </div>

                      {/* Human verification checkbox */}
                      <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border">
                        <Checkbox 
                          id="human" 
                          checked={isHuman}
                          onCheckedChange={setIsHuman}
                        />
                        <div className="flex items-center gap-2">
                          <ShieldCheck className="w-5 h-5 text-[#8B2942]" />
                          <Label htmlFor="human" className="text-sm font-medium cursor-pointer">
                            Je confirme ne pas être un robot
                          </Label>
                        </div>
                      </div>

                      <Button
                        type="submit"
                        size="lg"
                        className="w-full sm:w-auto bg-[#8B2942] hover:bg-[#6d2033]"
                        disabled={isSubmitting || !isHuman}
                      >
                        {isSubmitting ? (
                          'Envoi en cours...'
                        ) : (
                          <>
                            <Send className="w-5 h-5 mr-2" />
                            Envoyer le message
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
